/**
 * CHI PUÒ APRIRE COSA — la regola di visibilità del menu, e quella della pagina, sono la stessa.
 *
 * ## Il difetto che chiude
 *
 * Le voci di menu erano già filtrate per claim e per feature-flag, ma la **pagina** no: su 219
 * file di rotta, otto contengono un controllo. Chi digitava l'indirizzo, tornava su un vecchio
 * segnalibro o seguiva un link in una mail apriva lo stesso una schermata che non gli spetta —
 * e a fermarlo era il `403` sui dati, cioè una pagina che si compila a metà, con qualche
 * riquadro vuoto e nessuna frase che spieghi. Il sintomo somiglia a un guasto, non a un
 * divieto, e la differenza conta: da un guasto uno riprova, da un divieto chiede un permesso.
 *
 * ## ⚠️ Questo non è il muro
 *
 * Il muro è del server: ogni rotta risponde `403` sui dati a chi non ne ha diritto, e resta
 * così anche se questo file sparisse. Quello che si aggiunge qui è **una spiegazione**, non una
 * protezione. Va tenuto a mente al prossimo giro: nessuno deve poter pensare che spegnere un
 * controllo qui apra qualcosa.
 *
 * ## Perché legge la tabella del menu invece di avere una lista sua
 *
 * La condizione «questa voce si vede» esiste già, in `appNav`: claim, claim alternativi,
 * feature-flag di tenant. Scriverne una seconda per le pagine significherebbe due elenchi da
 * tenere allineati, e il giorno che divergono il sintomo è il peggiore — un menu che offre una
 * voce che la pagina rifiuta, o l'opposto. Qui si riusa la stessa riga di `NAV_SECTIONS`.
 */
import { hasClaim } from '@/shared/permissions/claims';
import { SECTIONS, sectionItems, type NavItem } from './appNav';

/**
 * Perché una pagina non si apre. Ognuno ha un rimedio diverso, ed è per questo che sono più di uno.
 *
 * ⚠️ Qui c'era anche `'masterOnly'` («Pagina della sola azienda principale»), con titolo, testo e
 * rimedio scritti — e `motivoDiniego` non l'ha **mai** restituito: nessun campo di `NavItem` lo
 * produceva, e nessuna pagina dell'applicativo esiste solo sull'azienda di piattaforma (il master
 * serve al branding ereditato, non a gatare rotte).
 *
 * Non era una mancanza, era una **promessa**: chi leggeva questo file concludeva che il caso
 * fosse coperto. È stato tolto invece che riempito, perché inventare la pagina che lo giustifica
 * sarebbe stato il rimedio sbagliato. Se un giorno servirà — il candidato naturale è nascondere le
 * pagine di sola gestione sull'azienda di piattaforma, dove chi non è super-admin comunque non
 * scrive (`masterTenant.ts`) — si riaggiunge **insieme** al campo che lo produce: la prova
 * `ogni motivo di diniego è raggiungibile` non lascia rientrare un valore da solo.
 */
export type DenialReason = 'permission' | 'module' | 'runtime';

/**
 * La parte di una voce che serve a questa domanda: dove porta e a quali condizioni.
 *
 * Più stretta di `NavItem` perché non tutte le pagine con un requisito sono voci di menu (vedi
 * `FUORI_MENU`), e chiedere a quelle un'etichetta e un'icona significherebbe inventarle.
 */
export type AccessEntry = Pick<NavItem, 'to' | 'claim' | 'claimAny' | 'flag' | 'flagAny' | 'soloMobile' | 'runtime'>;

export const DENY_REASONS: Record<DenialReason, { titolo: string; text: string; remedy: string }> = {
  permission: {
    titolo: 'Non hai i permessi per questa pagina',
    text: 'Il tuo profilo non comprende questa sezione.',
    remedy: 'Se pensi che sia un errore, chiedi a chi amministra la tua azienda di aggiungerti il permesso.',
  },
  runtime: {
    titolo: 'Pagina non disponibile qui',
    text: 'Questa pagina esiste solo nell’altro modo di usare il prodotto: quella installata sul telefono, o quella nel browser.',
    remedy: 'Aprila dall’applicazione se sei nel browser, o dal browser se sei nell’applicazione.',
  },
  module: {
    titolo: 'Sezione non attiva per questa azienda',
    text: 'Questa funzione non è compresa in quello che la tua azienda ha attivato: il sistema la rifiuta a chiunque, anche a chi amministra.',
    remedy: 'I moduli li attiva la piattaforma, o li spegne la capogruppo: in Amministrazione → Moduli attivi trovi quale dei due, e a chi chiedere.',
  },
};

/** Quello che serve sapere per decidere. Passato dall'esterno: qui dentro non ci sono hook. */
export interface AccessContext {
  claims: readonly string[];
  isSuperAdmin: boolean;
  /**
   * Dove sta girando questa copia: dentro all'applicazione installata o in una scheda del browser.
   *
   * ⚠️ Obbligatorio e senza valore predefinito **apposta**. Un `?? 'web'` avrebbe reso invisibili
   * le voci `runtime: 'app'` a chiunque dimenticasse di passarlo — comprese tutte le prove — e il
   * difetto sarebbe stato una voce che non compare, cioe' quello che non si vede.
   */
  runtime: 'web' | 'app';
  /** Il feature-flag di tenant. Default **acceso**: durante il caricamento il menu non sfarfalla. */
  flagOn: (flag: string, defaultValue: boolean) => boolean;
  /**
   * `false` finché non sappiamo *se* questa persona ha claim.
   *
   * ⚠️ Non è un dettaglio di comodo, è la differenza fra funzionare e no. I claim **non stanno nel
   * JWT**: arrivano nel `claimsList` della busta di login, e l'access token vive solo in memoria.
   * Su hard refresh, quindi, per il tempo del round-trip di refresh `decoded` è `null` e la lista
   * è vuota — e una lista vuota, per un `includes`, è indistinguibile da «non ha nessun permesso».
   * Senza questo campo un F5 su una pagina qualsiasi mostrerebbe per un istante la schermata «non
   * hai i permessi» a chi i permessi ce li ha, cioè il gating negherebbe proprio a tutti.
   *
   * Nel dubbio si concede: il menu mostra e la pagina si apre, come già fa il feature-flag col suo
   * default acceso. Il muro resta quello del server, che di dubbi non ne ha.
   */
  readyClaims: boolean;
}

/**
 * Tutte le voci, appiattite. Le sezioni servono al disegno del menu, non a questa domanda.
 *
 * Appiattite **una volta**: `vocePerPercorso` la interroga a ogni cambio di rotta e a ogni riga
 * della palette ⌘K, e rifare il `flatMap` ogni volta allocherebbe centoquattro oggetti per
 * rispondere a una domanda su una stringa.
 */
const ALL_ENTRIES: readonly NavItem[] = SECTIONS.flatMap(sectionItems);

/**
 * Pagine che **non stanno nel menu** e hanno comunque un requisito.
 *
 * Restano poche per costruzione, ed è voluto: l'albero è il posto dove il requisito si dichiara, e
 * una pagina fuori dal menu di solito è il dettaglio di una che c'è — quindi eredita dal prefisso.
 * Qui ci sono solo i casi in cui non esiste nessun prefisso da cui ereditare.
 */
const OUTSIDE_MENU: ReadonlyArray<AccessEntry> = [
  /* La pagina che si dichiarava «avanzata per superadmin» in una riga di testo e che nessuno
     faceva rispettare: il campo accetta un tipo qualsiasi scritto a mano, anche quelli fuori dal
     muro multi-tenant. Il claim di rotta esiste nel seed BE, è su `SuperAdmin` e `Developer` e su
     nessun altro, e la pagina lo legge già da sé (`features/admin/GeneralCrud.tsx`): sta qui
     perché la palette ⌘K la offriva a chiunque, cioè insegnava che esisteva. */
  { to: '/general-crud', claim: 'r-p-general-crud' },
  /* Le tre rotte assorbite da «Policy aziendali» (`/admin/policies`), che nel menu non hanno più
     una voce propria. Rispondono ancora — sono nei segnalibri e nella documentazione — e senza
     queste righe resterebbero le sole pagine di configurazione senza gate: `vocePerPercorso` non
     troverebbe nessun prefisso da cui ereditare e `motivoDiniego(null)` concede. Il muro sarebbe
     comunque quello del server, ma chi non ha il claim vedrebbe una pagina vuota invece della
     spiegazione. */
  { to: '/admin/tardiness-policies', claim: 'TardinessPolicy.update' },
  { to: '/admin/smart-working-config', claim: 'SmartWorkingPolicy.update', flag: 'smartWorking.enabled' },
  { to: '/admin/planning-policies', claimAny: ['PlanningPolicy.update'] },
  /* Le rotte assorbite dall'hub «Assistenza» (`/support`, 11-09-2026). Le prime due rimandano alla
     scheda giusta e portano il gate della scheda; `/tickets` è l'archivio del vecchio help desk,
     senza più voce ma ancora consultabile. `Ticket.read` sul dev ce l'hanno tutte le utenze e a
     catalogo nessuna: il gate sta sul claim vero e il debito è dichiarato; `r-p-help-desk-admin` è la
     seconda strada, quella dei ruoli legacy. */
  { to: '/support-intake', claimAny: ['support.intake.work', 'SupportIntake.read'], flag: 'support.enabled' },
  { to: '/admin/support-topics', claim: 'SupportTopic.update', flag: 'support.enabled' },
  { to: '/tickets', claimAny: ['Ticket.read', 'r-p-help-desk-admin'] },
];

/**
 * La voce di menu che governa un indirizzo: la più **lunga** fra quelle che lo prefissano.
 *
 * Il confronto è per prefisso di segmento e non per uguaglianza, o `/admin/tenants/<guid>`
 * non troverebbe la voce `/admin/tenants`. Ed è la più lunga perché `/timesheet/admin` deve
 * vincere su `/timesheet`: la sotto-pagina è spesso più ristretta della sezione.
 */
export function entryByPath(pathname: string): AccessEntry | null {
  let best: AccessEntry | null = null;
  for (const it of [...ALL_ENTRIES, ...OUTSIDE_MENU]) {
    if (!it.to) continue;
    if (pathname !== it.to && !pathname.startsWith(`${it.to}/`)) continue;
    if (!best || it.to.length > (best.to?.length ?? 0)) best = it;
  }
  return best;
}

/**
 * I claim che questo indirizzo pretende, per nome.
 *
 * Serve alla schermata di diniego: «non hai i permessi» da solo manda a chiedere *un* permesso
 * senza saper dire quale, e chi amministra non può concedere ciò che non gli viene nominato.
 */
export function claimRequired(pathname: string): string[] {
  const v = entryByPath(pathname);
  if (!v) return [];
  return [...(v.claim ? [v.claim] : []), ...(v.claimAny ?? [])];
}

/**
 * Perché questa voce non si può aprire, o `null` se si può.
 *
 * Il super-admin passa **per regola**, come sul server: è un bypass, non un elenco di claim da
 * tenere aggiornato. L'ordine conta — prima il modulo, poi il permesso: a chi lavora in
 * un'azienda che non ha comprato la flotta si dice «non è attiva», non «non hai i permessi»,
 * che manderebbe a chiedere un permesso che nessuno può dargli.
 */
export function denialReason(item: AccessEntry | null, ctx: AccessContext): DenialReason | null {
  if (!item) return null;
  /* Il guscio prima di tutto, super-admin compreso: non e' un permesso, e' dove sei. Una pagina
     che vive di NFC non si apre in una scheda del browser nemmeno se puoi tutto. */
  if (item.runtime && item.runtime !== ctx.runtime) return 'runtime';
  if (item.flag && !ctx.flagOn(item.flag, true)) return 'module';
  if (item.flagAny && !item.flagAny.some((f) => ctx.flagOn(f, true))) return 'module';
  if (ctx.isSuperAdmin) return null;
  /* Claim non ancora noti: si concede. Vedi `claimsPronti` — è la finestra fra il primo render di
     un reload e la risposta del refresh, in cui «non li ha» e «non lo so ancora» hanno la stessa
     forma e conseguenze opposte. */
  if (!ctx.readyClaims) return null;
  /* `hasClaim` e non `includes`: copre i quattro nomi rinominati dal BE il 06-08-2026, che un token
     emesso prima del deploy continua a portare finché non scade (`shared/permissions/claims.ts`).
     Con `includes` chi ha `canSeeAllUsersAbsence` sul token vecchio si vedrebbe negare una pagina
     che gli spetta. */
  if (item.claim && !hasClaim(ctx.claims, item.claim)) return 'permission';
  if (item.claimAny && !item.claimAny.some((c) => hasClaim(ctx.claims, c))) return 'permission';
  return null;
}

/**
 * La voce si vede nel menu?
 *
 * Stessa regola della pagina più una cosa che **non** è un permesso: `soloMobile` toglie dalla
 * colonna le voci che su desktop sono già una scheda di un'altra sezione. Per questo il filtro
 * sta prima di tutto, anche del super-admin.
 */
export function visibleEntry(item: NavItem, ctx: AccessContext): boolean {
  if (item.soloMobile) return false;
  return denialReason(item, ctx) === null;
}
